import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet } from '../../lib/api';
import { toast } from 'sonner';
import { BarChart3, ArrowUpRight } from 'lucide-react';

const METRICS = [
  { value: 'margin', label: 'Margin %', positive: true },
  { value: 'progress', label: 'Progress %', positive: true },
  { value: 'cost_overrun', label: 'Cost Overrun %', positive: false },
  { value: 'labour_productivity', label: 'Labour Productivity', positive: true },
];

const fmtPct = (n) => `${(Number(n) || 0).toFixed(1)}%`;

export default function CompareProjects() {
  const [metric, setMetric] = useState('margin');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiGet(`/mis/compare?metric=${metric}`)
      .then((r) => { if (!cancelled) setData(r); })
      .catch(() => toast.error('Compare failed'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [metric]);

  const cur = METRICS.find((m) => m.value === metric);
  const rows = data?.rows || [];
  const avg = data?.company_average ?? 0;
  // Bar scale — use max absolute value so negative cost-overrun also visualises.
  const maxAbs = Math.max(1, ...rows.map((r) => Math.abs(r[metric] || 0)));

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="compare-projects-page">
      <h1 className="text-2xl font-bold text-slate-900 font-display mb-1">Compare Projects</h1>
      <p className="text-xs text-slate-500 mb-4">
        Rank every active project on a single metric vs the company average.
      </p>

      <div className="flex items-center gap-2 mb-5">
        <span className="text-xs uppercase tracking-wider font-bold text-slate-500">Metric</span>
        <select
          value={metric}
          onChange={(e) => setMetric(e.target.value)}
          data-testid="compare-metric-select"
          className="border border-slate-300 rounded px-2 py-1.5 text-sm bg-white"
        >
          {METRICS.map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
        {data && (
          <span className="ml-auto text-sm text-slate-600">
            Company Avg: <b className="text-indigo-700 tabular-nums">{fmtPct(avg)}</b>
          </span>
        )}
      </div>

      <div className="bg-white border rounded-lg p-4 shadow-sm">
        {loading ? (
          <div className="text-slate-500 text-sm py-8 text-center">Computing rankings…</div>
        ) : rows.length === 0 ? (
          <div className="text-slate-500 text-sm py-8 text-center">No active projects.</div>
        ) : (
          <table className="w-full" data-testid="compare-table">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-slate-500 border-b">
                <th className="text-left py-2 w-12">#</th>
                <th className="text-left py-2">Project</th>
                <th className="text-right py-2 w-32">{cur.label}</th>
                <th className="py-2 w-1/2 pl-4">Distribution</th>
                <th className="py-2 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const v = r[metric] || 0;
                const pct = (Math.abs(v) / maxAbs) * 100;
                const above = cur.positive ? v >= avg : v <= avg;
                return (
                  <tr key={r.project_id} className="border-b last:border-0 hover:bg-slate-50">
                    <td className="py-2 text-sm text-slate-500">{i + 1}</td>
                    <td className="py-2 text-sm font-medium text-slate-800">{r.name}</td>
                    <td className={`py-2 text-right text-sm font-bold tabular-nums ${above ? 'text-emerald-700' : 'text-rose-700'}`}>
                      {fmtPct(v)}
                    </td>
                    <td className="py-2 pl-4">
                      <div className="relative h-3 bg-slate-100 rounded overflow-hidden">
                        <div
                          className={above ? 'absolute inset-y-0 left-0 bg-emerald-500' : 'absolute inset-y-0 left-0 bg-rose-500'}
                          style={{ width: `${Math.min(100, pct)}%` }}
                        />
                        {/* avg marker */}
                        <div
                          className="absolute inset-y-0 w-px bg-indigo-800"
                          style={{ left: `${Math.min(100, (Math.abs(avg) / maxAbs) * 100)}%` }}
                          title={`Avg ${fmtPct(avg)}`}
                        />
                      </div>
                    </td>
                    <td className="py-2">
                      <Link
                        to={`/app/mis/project/${r.project_id}`}
                        className="text-indigo-700 hover:text-indigo-900"
                        title="Open deep-dive"
                      >
                        <ArrowUpRight size={16} />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <p className="text-[11px] text-slate-400 italic mt-3">
          <BarChart3 size={11} className="inline mb-0.5" /> Bars scaled to the largest value;
          vertical line = company average. Positive metrics (margin, progress, labour
          productivity) — above avg is green. Cost overrun — below avg is green.
        </p>
      </div>
    </div>
  );
}
